import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';

interface PricingTier {
  tier: string;
  tweet_count: number;
  description: string;
  download_cost: number;
  service_fee: number;
  total_usd: number;
  price_usd: number;
  price_sats: number;
  coverage: string;
  available: boolean;
  recommended?: boolean;
}

interface PricingData {
  x_handle: string;
  total_tweets: number;
  tiers: PricingTier[];
}

interface PaymentData {
  payment_hash: string;
  invoice: string;
  amount_sats: number;
  amount_usd: number;
  expires_at: string;
  tier: string;
  tweet_count: number;
}

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%);
  color: white;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  max-width: 600px;
`;

const Title = styled.h1`
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  margin-bottom: 15px;
  background: linear-gradient(135deg, #f7931a 0%, #ffb74d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #aaa;
  margin-bottom: 10px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin: 20px 0;
  padding: 15px 25px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid #f7931a;
`;

const UserDetails = styled.div`
  text-align: left;
`;

const Username = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
`;

const TweetCount = styled.div`
  font-size: 0.9rem;
  color: #888;
`;

const TiersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 500px;
  margin-bottom: 40px;
`;

const TierCard = styled.div<{ recommended?: boolean }>`
  background: ${props => props.recommended ? 
    'linear-gradient(135deg, rgba(247, 147, 26, 0.15) 0%, rgba(255, 183, 77, 0.1) 100%)' : 
    'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.recommended ? '#f7931a' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  padding: 25px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(247, 147, 26, 0.3);
    border-color: #f7931a;
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background: linear-gradient(135deg, #f7931a 0%, #ffb74d 100%);
  color: #000;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const TierName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 10px;
  color: #fff;
`;

const TierDescription = styled.p`
  font-size: 0.9rem;
  color: #aaa;
  margin-bottom: 20px;
`;

const PriceBreakdown = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  font-size: 0.85rem;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #ccc;
`;

const PriceTotal = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.1rem;
  font-weight: 700;
  color: #f7931a;
`;

const UnlockButton = styled.button`
  width: 100%;
  padding: 16px;
  font-size: 1rem;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #f7931a 0%, #ffb74d 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(247, 147, 26, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 26, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #f7931a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.3);
  color: #ff6b6b;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 20px;
  text-align: center;
`;

const FAQ = styled.div`
  max-width: 600px;
  margin-top: 40px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FAQTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 20px;
  color: #f7931a;
`;

const FAQItem = styled.div`
  margin-bottom: 20px;
`;

const FAQQuestion = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 1rem;
`;

const FAQAnswer = styled.div`
  color: #aaa;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ProgressContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  position: relative;
  padding: 0 10px;
`;

const ProgressLine = styled.div`
  position: absolute;
  top: 20px;
  left: 10%;
  right: 10%;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 0;
`;

const ProgressStep = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
  flex: 1;
`;

const StepCircle = styled.div<{ active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? 'linear-gradient(135deg, #f7931a 0%, #ffb74d 100%)' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => props.active ? '#f7931a' : 'rgba(255, 255, 255, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${props => props.active ? '#fff' : '#666'};
  font-size: 0.9rem;
  transition: all 0.3s ease;
`;

const StepLabel = styled.div<{ active: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.active ? '#f7931a' : '#666'};
  font-weight: ${props => props.active ? '600' : '400'};
  text-align: center;
  transition: all 0.3s ease;
`;

const EstimatedTime = styled.div`
  text-align: center;
  margin-top: 12px;
  padding: 8px 16px;
  background: rgba(247, 147, 26, 0.1);
  border-radius: 8px;
  color: #f7931a;
  font-size: 0.85rem;
  font-weight: 600;
`;

const Pricing: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'expired'>('pending');
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [currentTweetCount, setCurrentTweetCount] = useState(0);
  const [btcPrice, setBtcPrice] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState('Waiting for payment...');
  const [progressStage, setProgressStage] = useState(0);
  const [tweetsProgress, setTweetsProgress] = useState(0);
  const [totalTweets, setTotalTweets] = useState(0);
  const [notificationEmail, setNotificationEmail] = useState('');
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  
  // Waitlist state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [waitlistStatus, setWaitlistStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [waitlistMessage, setWaitlistMessage] = useState('');

  // Email waitlist signup handler
  const submitWaitlist = async () => {
    if (!waitlistEmail.trim() || !waitlistEmail.includes('@')) {
      setWaitlistStatus('error');
      setWaitlistMessage('Please enter a valid email address');
      return;
    }

    setWaitlistStatus('loading');
    try {
      const response = await fetch('/api/auth/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: waitlistEmail.trim(),
          newsletter_opt_in: newsletterOptIn,
          source: 'pricing_oauth_error'
        })
      });

      const data = await response.json();
      if (data.success) {
        setWaitlistStatus('success');
        setWaitlistMessage('🎉 You\'re on the list! We\'ll notify you of updates.');
      } else {
        setWaitlistStatus('error');
        setWaitlistMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setWaitlistStatus('error');
      setWaitlistMessage('Network error. Please try again.');
    }
  };

  // Helper function to calculate estimated processing time
  const getEstimatedTime = (tweetCount: number): number => {
    const downloadTime = Math.ceil(tweetCount / 1500); // ~1500 tweets/min
    const analysisTime = 3; // ~3 min for AI analysis
    return downloadTime + analysisTime;
  };

  // Progress bar component
  const ProgressBar: React.FC<{ stage: number }> = ({ stage }) => {
    const stages = ['Payment', 'Download', 'Analysis', 'Complete'];
    return (
      <>
        <ProgressContainer>
          <ProgressLine />
          {stages.map((label, idx) => (
            <ProgressStep key={idx} active={idx <= stage}>
              <StepCircle active={idx <= stage}>
                {idx < stage ? '✓' : idx + 1}
              </StepCircle>
              <StepLabel active={idx <= stage}>{label}</StepLabel>
            </ProgressStep>
          ))}
        </ProgressContainer>
        {stage > 0 && paymentData && (
          <EstimatedTime>
            ⏱️ Estimated time: ~{getEstimatedTime(paymentData.tweet_count)} minutes
          </EstimatedTime>
        )}
      </>
    );
  };

  useEffect(() => {
    // Get token from URL or localStorage
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const upgradeParam = params.get('upgrade');
    const oauthError = params.get('error');
    const processingParam = params.get('processing');
    const paymentHashParam = params.get('payment_hash');
    
    // Handle OAuth errors with friendly messages
    if (oauthError) {
      const errorMessages: Record<string, string> = {
        'missing_oauth_params': 'Sign in was cancelled or incomplete. Please try again.',
        'oauth_token_expired': 'Your sign in session expired. Please try again.',
        'authentication_failed': 'Sign in failed. Please try again or use a demo account.',
      };
      setError(errorMessages[oauthError] || 'Sign in failed. Please try again.');
      setLoading(false);
      // Clean up URL
      window.history.replaceState({}, document.title, '/pricing');
      return;
    }
    
    // Handle returning user with processing payment
    if (processingParam === 'true' && paymentHashParam) {
      setPaymentStatus('paid');
      setStatusMessage('Your payment was received! Processing your tweets...');
      setProgressStage(1);
      // Start monitoring the existing payment
      monitorPayment(paymentHashParam);
    }
    
    // Check if this is an upgrade request
    if (upgradeParam === 'true') {
      setIsUpgrade(true);
    }
    
    if (urlToken) {
      localStorage.setItem('jwt_token', urlToken);
      setToken(urlToken);
      
      // Decode JWT to get profile image (simple base64 decode, not cryptographic)
      try {
        const payload = JSON.parse(atob(urlToken.split('.')[1]));
        setProfileImage(payload.profile_image);
      } catch (e) {
        console.error('Failed to decode JWT:', e);
      }
      
      // Clean up URL
      window.history.replaceState({}, document.title, '/pricing');
    } else {
      const storedToken = localStorage.getItem('jwt_token');
      if (storedToken) {
        setToken(storedToken);
        
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          setProfileImage(payload.profile_image);
        } catch (e) {
          console.error('Failed to decode JWT:', e);
        }
      } else {
        setError('No authentication token found. Please sign in.');
        setLoading(false);
        return;
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchBtcPrice();
      fetchPricing();
    }
  }, [token]);

  const fetchBtcPrice = async () => {
    try {
      // Fetch from CoinGecko API (free, no auth required)
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd');
      const data = await response.json();
      setBtcPrice(data.bitcoin.usd);
    } catch (error) {
      console.error('Failed to fetch BTC price:', error);
      // Fallback to approximate price
      setBtcPrice(100000);
    }
  };

  const fetchPricing = async () => {
    if (!token) return;

    try {
      // Decode JWT to get x_handle
      const payload = JSON.parse(atob(token.split('.')[1]));
      const x_handle = payload.x_handle;

      const response = await fetch(`/api/payments/pricing?x_handle=${x_handle}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        const data = await response.json();
        if (data.expired) {
          setError('Your session has expired. Please sign in again.');
          localStorage.removeItem('jwt_token');
          setTimeout(() => window.location.href = '/api/auth/x/login', 2000);
        } else {
          setError('Authentication failed. Please sign in again.');
        }
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch pricing');
      }

      const data = await response.json();
      setPricingData(data);
      
      // Store current tweet count for upgrade messaging
      if (data.current_tweet_count) {
        setCurrentTweetCount(data.current_tweet_count);
      }
      
      // Override isUpgrade if backend says it's an upgrade
      if (data.is_upgrade) {
        setIsUpgrade(true);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Pricing fetch error:', err);
      setError('Failed to load pricing. Please try again.');
      setLoading(false);
    }
  };

  const handleUnlock = async (tier: PricingTier) => {
    if (!token || !pricingData) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/payments/create-wrapped-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          x_handle: pricingData.x_handle,
          tier: tier.tier,
          email: notificationEmail || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create invoice');
      }

      const data = await response.json();
      setPaymentData(data);
      
      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(data.invoice, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
      
      // Start payment monitoring
      monitorPayment(data.payment_hash);
      
      setLoading(false);
    } catch (err) {
      console.error('Invoice creation error:', err);
      setError('Failed to create invoice. Please try again.');
      setLoading(false);
    }
  };

  const monitorPayment = async (payment_hash: string) => {
    const checkPayment = async () => {
      try {
        const response = await fetch(`/api/payments/wrapped-status/${payment_hash}`);
        if (response.ok) {
          const data = await response.json();
          
          // Update progress based on status flags
          if (data.paid && !data.tweets_downloaded) {
            setPaymentStatus('paid');
            setTotalTweets(data.tweet_count || 0);
            setTweetsProgress(data.tweets_progress || 0);
            const progressText = data.tweets_progress > 0 
              ? `📥 Downloaded ${data.tweets_progress.toLocaleString()} / ${data.tweet_count.toLocaleString()} tweets...`
              : 'Payment confirmed! Starting tweet download...';
            setStatusMessage(progressText);
            setProgressStage(1);
          } else if (data.tweets_downloaded && !data.wrapped_generated) {
            setStatusMessage('✅ Tweets downloaded! Generating AI analysis...');
            setProgressStage(2);
          } else if (data.wrapped_generated) {
            setStatusMessage('🎉 Wrapped ready! Redirecting...');
            setProgressStage(3);
            setTimeout(() => {
              if (pricingData) {
                window.location.href = `/wrapped?user=${pricingData.x_handle}&authenticated=true`;
              }
            }, 1000);
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Error checking payment:', error);
        return false;
      }
    };

    const maxAttempts = 600; // Increased from 300 to allow for longer processing
    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;
      const completed = await checkPayment();

      if (completed || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts && paymentStatus !== 'paid') {
          setPaymentStatus('expired');
          setStatusMessage('Invoice expired. Please try again.');
        }
      }
    }, 1000); // Changed from 2000ms to 1000ms for more responsive updates
  };

  if (loading && !pricingData) {
    return (
      <Container>
        <LoadingSpinner />
      </Container>
    );
  }

  if (error && !pricingData) {
    return (
      <Container>
        <Header>
          <Title>⚠️ Sign In Issue</Title>
          <Subtitle>{error}</Subtitle>
        </Header>
        <UnlockButton 
          onClick={() => window.location.href = '/api/auth/x/login'}
          style={{ maxWidth: '320px', marginBottom: '20px' }}
        >
          <svg 
            viewBox="0 0 24 24" 
            style={{ width: '18px', height: '18px', fill: 'currentColor', marginRight: '8px' }}
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          Try Signing In Again
        </UnlockButton>
        
        {/* Email waitlist for non-X users */}
        <div style={{ 
          marginTop: '20px', 
          padding: '25px', 
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          maxWidth: '350px',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            color: '#fff', 
            marginBottom: '10px',
            fontWeight: '600'
          }}>
            No X account?
          </h3>
          <p style={{ 
            fontSize: '0.85rem', 
            color: '#aaa', 
            marginBottom: '15px',
            lineHeight: '1.4'
          }}>
            Join our waitlist to get notified when we support more platforms!
          </p>
          
          {waitlistStatus === 'success' ? (
            <p style={{ 
              fontSize: '0.9rem', 
              color: '#4CAF50', 
              padding: '12px',
              background: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '8px'
            }}>
              {waitlistMessage}
            </p>
          ) : (
            <>
              <input
                type="email"
                placeholder="your@email.com"
                value={waitlistEmail}
                onChange={(e) => {
                  setWaitlistEmail(e.target.value);
                  if (waitlistStatus === 'error') setWaitlistStatus('idle');
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '0.9rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: waitlistStatus === 'error' ? '1px solid #ff6b6b' : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  marginBottom: '10px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onKeyPress={(e) => e.key === 'Enter' && submitWaitlist()}
              />
              
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '8px',
                fontSize: '0.8rem',
                color: '#aaa',
                marginBottom: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    accentColor: '#f7931a'
                  }}
                />
                Keep me updated on new features
              </label>
              
              {waitlistStatus === 'error' && (
                <p style={{ 
                  fontSize: '0.8rem', 
                  color: '#ff6b6b', 
                  marginBottom: '10px' 
                }}>
                  {waitlistMessage}
                </p>
              )}
              
              <UnlockButton
                onClick={submitWaitlist}
                disabled={waitlistStatus === 'loading'}
                style={{
                  fontSize: '0.9rem',
                  padding: '12px 20px',
                  width: '100%',
                  opacity: waitlistStatus === 'loading' ? 0.7 : 1
                }}
              >
                {waitlistStatus === 'loading' ? 'Joining...' : '📧 Join Waitlist'}
              </UnlockButton>
            </>
          )}
        </div>
      </Container>
    );
  }

  if (!pricingData) return null;

  // Filter tiers for upgrade mode - only show tiers with more tweets than currently analyzed
  const displayTiers = isUpgrade 
    ? pricingData.tiers.filter(tier => tier.tweet_count > currentTweetCount)
    : pricingData.tiers;

  return (
    <Container>
      <Header>
        <Title>{isUpgrade ? '⚡ Upgrade Your Analysis' : '🎁 Unlock Your Bitcoin Wrapped'}</Title>
        <Subtitle>
          {isUpgrade 
            ? `Currently analyzing ${currentTweetCount.toLocaleString()} tweets. Upgrade for deeper insights!`
            : 'Choose your tier + get a personal dashboard to manage your Bitcoin predictions'
          }
        </Subtitle>
        
        <UserInfo>
          {profileImage && <Avatar src={profileImage} alt={pricingData.x_handle} />}
          <UserDetails>
            <Username>@{pricingData.x_handle}</Username>
            <TweetCount>{pricingData.total_tweets.toLocaleString()} total tweets</TweetCount>
          </UserDetails>
        </UserInfo>
      </Header>

      {displayTiers.length > 0 ? (
      <>
        {/* Email collection */}
        <div style={{
          maxWidth: '500px',
          margin: '0 auto 24px auto',
          padding: '16px',
          background: 'rgba(247, 147, 26, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(247, 147, 26, 0.2)'
        }}>
          <div style={{ 
            fontSize: '0.85rem', 
            color: '#f7931a', 
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            📧 Get notified when your Wrapped is ready (optional)
          </div>
          <input
            type="email"
            placeholder="your@email.com"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(0, 0, 0, 0.3)',
              color: '#fff',
              fontSize: '0.9rem',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ 
            fontSize: '0.7rem', 
            color: '#888', 
            marginTop: '6px' 
          }}>
            We'll also send you a link to your personal dashboard
          </div>
        </div>
        
      <TiersContainer>
        {displayTiers.map((tier) => (
          <TierCard key={tier.tier} recommended={tier.recommended}>
            {tier.recommended && <RecommendedBadge>{displayTiers.length === 1 ? 'Best Value' : 'Recommended'}</RecommendedBadge>}
            
            <TierName>{tier.tier}</TierName>
            <TierDescription>
              {tier.description} ({tier.coverage} coverage)
            </TierDescription>

            <PriceBreakdown>
              <PriceRow>
                <span>Tweet Analysis ({tier.tweet_count.toLocaleString()} tweets)</span>
                <span>${tier.download_cost.toFixed(2)}</span>
              </PriceRow>
              <PriceRow>
                <span>Service Fee (30%)</span>
                <span>+${tier.service_fee.toFixed(2)}</span>
              </PriceRow>
              <PriceTotal>
                <span>Total</span>
                <span>${tier.price_usd} ({tier.price_sats.toLocaleString()} sats)</span>
              </PriceTotal>
            </PriceBreakdown>

            <UnlockButton 
              onClick={() => handleUnlock(tier)}
              disabled={!tier.available || loading}
            >
              {loading ? 'Creating Invoice...' : `⚡ Unlock for ${tier.price_sats.toLocaleString()} sats`}
            </UnlockButton>
          </TierCard>
        ))}
      </TiersContainer>
      </>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          background: 'rgba(76, 175, 80, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          maxWidth: '500px',
          margin: '40px auto'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✨</div>
          <h3 style={{ color: '#fff', marginBottom: '8px', fontSize: '1.2rem' }}>Already maxed out!</h3>
          <p style={{ color: '#aaa', marginBottom: '20px' }}>
            You're already analyzing the maximum number of tweets available in our system. 
            You have access to your complete Twitter history! 🎉
          </p>
          <UnlockButton onClick={() => window.history.back()}>
            ← Go Back
          </UnlockButton>
        </div>
      )}

      <FAQ>
        <FAQTitle>💡 How It Works</FAQTitle>
        
        <FAQItem>
          <FAQQuestion>🔐 Why do I need to sign in?</FAQQuestion>
          <FAQAnswer>
            We use X OAuth to verify your identity and calculate pricing based on your actual tweet count. We never see your password.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>💰 Why dynamic pricing?</FAQQuestion>
          <FAQAnswer>
            Fair pricing: You only pay for what we analyze. Downloading 1,000 tweets costs less than 10,000 tweets. We add 30% service fee to cover infrastructure.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>⚡ What is Lightning payment?</FAQQuestion>
          <FAQAnswer>
            Lightning is instant Bitcoin payments. Scan the QR code with any Lightning wallet (Strike, Cash App, Phoenix, etc.). Payment confirms in seconds.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>⏱️ How long does it take?</FAQQuestion>
          <FAQAnswer>
            After payment, we download and analyze your tweets. This takes 2-5 minutes depending on tweet count. You'll see your wrapped automatically when ready.
          </FAQAnswer>
        </FAQItem>
      </FAQ>

      {paymentData && qrCodeDataUrl && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ color: '#fff', textAlign: 'center', marginBottom: '24px', fontSize: '1.8rem' }}>
              ⚡ Lightning Payment
            </h2>

            {paymentStatus === 'pending' && (
              <>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#f7931a',
                    marginBottom: '8px'
                  }}>
                    {paymentData.amount_sats.toLocaleString()} sats
                  </div>
                  <div style={{
                    fontSize: '1.1rem',
                    color: '#999'
                  }}>
                    ≈ ${paymentData.amount_usd} USD
                  </div>
                </div>
                
                <ProgressBar stage={progressStage} />
                
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  background: progressStage === 0 ? 'rgba(247, 147, 26, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                  color: progressStage === 0 ? '#f7931a' : '#22c55e',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  marginBottom: progressStage === 1 && tweetsProgress > 0 ? '8px' : '20px',
                  marginTop: '20px'
                }}>
                  {statusMessage}
                </div>
                
                {/* Tweet download progress bar */}
                {progressStage === 1 && totalTweets > 0 && (
                  <div style={{ marginBottom: '20px', padding: '0 20px' }}>
                    <div style={{
                      height: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.min(100, (tweetsProgress / totalTweets) * 100)}%`,
                        background: 'linear-gradient(90deg, #f7931a, #ffb347)',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <div style={{
                      textAlign: 'center',
                      fontSize: '0.8rem',
                      color: '#888',
                      marginTop: '4px'
                    }}>
                      {Math.round((tweetsProgress / totalTweets) * 100)}% complete
                    </div>
                  </div>
                )}
                
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <img src={qrCodeDataUrl} alt="Payment QR Code" style={{
                    maxWidth: '256px',
                    width: '100%',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }} />
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: '#a0a0a0',
                  maxHeight: '120px',
                  overflowY: 'auto'
                }}>
                  {paymentData.invoice}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(paymentData.invoice);
                    alert('Invoice copied!');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(247, 147, 26, 0.1)',
                    border: '1px solid #f7931a',
                    color: '#f7931a',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}
                >
                  📋 Copy Invoice
                </button>
                <button
                  onClick={() => {
                    setPaymentData(null);
                    setQrCodeDataUrl('');
                    setPaymentStatus('pending');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
              </>
            )}

            {paymentStatus === 'paid' && progressStage < 3 && (
              <>
                <ProgressBar stage={progressStage} />
                
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  borderRadius: '8px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  color: '#22c55e',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '20px',
                  marginTop: '20px'
                }}>
                  {statusMessage}
                </div>
                <div style={{ textAlign: 'center', color: '#a0a0a0', fontSize: '0.9rem' }}>
                  Please keep this window open while we process your data
                </div>
              </>
            )}

            {paymentStatus === 'expired' && (
              <>
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '20px'
                }}>
                  ⏰ Invoice Expired
                </div>
                <button
                  onClick={() => {
                    setPaymentData(null);
                    setQrCodeDataUrl('');
                    setPaymentStatus('pending');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Try Again
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Container>
  );
};

export default Pricing;
